import { useState, useEffect } from "react";
import Dialog from "../../../designSystem/c-organism/dialogs/Dialog";
import Button from "../../../designSystem/b-molecules/buttons/Button";
import { ref, onValue, off, update, get } from "firebase/database";
import { db } from "../../../configs/Firebase";
import style from "./JourneyOwnersDialog.module.css";
import Icon from "../../../designSystem/a-atoms/icons/Icon";

const JourneyOwnersDialog = ({ show, onClose, journeyId, orgId }) => {
  const [selectedOwnerIds, setSelectedOwnerIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [userProfiles, setUserProfiles] = useState({});
  const [saving, setSaving] = useState(false);

  // Load organization users
  useEffect(() => {
    if (orgId) {
      const usersRef = ref(db, `userData/${orgId}/users/`);
      
      const unsubscribe = onValue(usersRef, async (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const usersList = [];
          const profiles = {};
          
          // Process each user
          for (const emailKey in data) {
            const userData = data[emailKey];
            if (userData.id) {
              // Fetch profile data
              try {
                const profileSnapshot = await get(ref(db, `userData/${userData.id}/profile`));
                const profile = profileSnapshot.val() || {};
                
                const displayName = profile.displayName || userData.email || "";
                const initials = displayName
                  ? displayName.split(" ")[0].charAt(0) + 
                    (displayName.split(" ")[1] ? displayName.split(" ")[1].charAt(0) : "")
                  : (userData.email ? userData.email.charAt(0).toUpperCase() : "");
                
                profiles[userData.id] = {
                  displayName,
                  email: userData.email,
                  microsoftPhotoURL: profile.microsoftPhotoURL || null,
                  initials
                };
                
                usersList.push({
                  id: userData.id,
                  email: userData.email,
                  ...profiles[userData.id]
                });
              } catch (error) {
                console.error(`Error loading profile for user ${userData.id}:`, error);
                // Add user without profile data
                const email = userData.email || "";
                profiles[userData.id] = {
                  displayName: email,
                  email: email,
                  microsoftPhotoURL: null,
                  initials: email.charAt(0).toUpperCase()
                };
                usersList.push({
                  id: userData.id,
                  email: email,
                  ...profiles[userData.id]
                });
              }
            }
          }
          
          // Sort users by display name
          usersList.sort((a, b) => {
            const nameA = a.displayName || a.email || "";
            const nameB = b.displayName || b.email || "";
            return nameA.localeCompare(nameB);
          });
          
          setUsers(usersList);
          setUserProfiles(profiles);
        } else {
          setUsers([]);
          setUserProfiles({});
        }
      });

      return () => {
        off(usersRef);
      };
    }
  }, [orgId]);

  // Load current owners for this journey
  useEffect(() => {
    if (orgId && journeyId) {
      const ownersRef = ref(db, `userData/${orgId}/journeys/${journeyId}/owners`);
      
      const unsubscribe = onValue(ownersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const ownerIds = Object.keys(data);
          setSelectedOwnerIds(ownerIds);
        } else {
          setSelectedOwnerIds([]);
        }
      });

      return () => {
        off(ownersRef);
      };
    }
  }, [orgId, journeyId]);

  const handleToggleOwner = (userId) => {
    if (selectedOwnerIds.includes(userId)) {
      setSelectedOwnerIds(selectedOwnerIds.filter(id => id !== userId));
    } else {
      setSelectedOwnerIds([...selectedOwnerIds, userId]);
    }
  };

  const handleSave = async () => {
    if (!orgId || !journeyId) return;

    setSaving(true);
    try {
      // Get current owners
      const currentOwnersRef = ref(db, `userData/${orgId}/journeys/${journeyId}/owners`);
      const currentSnapshot = await get(currentOwnersRef);
      const currentData = currentSnapshot.val() || {};
      const currentOwnerIds = Object.keys(currentData);

      // Build updates
      const updates = {};
      
      // Add new owners
      for (const userId of selectedOwnerIds) {
        if (!currentOwnerIds.includes(userId)) {
          updates[`userData/${orgId}/journeys/${journeyId}/owners/${userId}`] = {
            uid: userId,
            assignedAt: { ".sv": "timestamp" }
          };
        }
      }

      // Remove owners that are no longer selected
      for (const userId of currentOwnerIds) {
        if (!selectedOwnerIds.includes(userId)) {
          updates[`userData/${orgId}/journeys/${journeyId}/owners/${userId}`] = null;
        }
      }

      // Apply updates
      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }

      onClose();
    } catch (error) {
      console.error("Error saving journey owners:", error);
      alert("Failed to save journey owners");
      setSaving(false);
    }
  };

  return (
    <Dialog
      show={show}
      onClose={onClose}
      title="Journey Owners"
      variants={["large"]}
    >
      <div className={style.dialog}>
        <p className={style.description}>
          Select users to assign as owners of this journey. Owners will be displayed in the title bar and journeys list.
        </p>
        
        <div className={style.userList}>
          {users.map((user) => {
            const isSelected = selectedOwnerIds.includes(user.id);
            return (
              <div
                key={user.id}
                className={`${style.userItem} ${isSelected ? style.selected : ""}`}
                onClick={() => handleToggleOwner(user.id)}
              >
                <div
                  className={style.avatar}
                  style={
                    user.microsoftPhotoURL
                      ? {
                          backgroundImage: `url(${user.microsoftPhotoURL})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                          color: "transparent",
                        }
                      : {}
                  }
                >
                  {!user.microsoftPhotoURL && user.initials}
                </div>
                <div className={style.userInfo}>
                  <div className={style.userName}>{user.displayName || user.email}</div>
                  {user.displayName && <div className={style.userEmail}>{user.email}</div>}
                </div>
                {isSelected && (
                  <Icon className={style.checkIcon}>check_circle</Icon>
                )}
              </div>
            );
          })}
          {users.length === 0 && (
            <div className={style.emptyState}>No users found in this organization.</div>
          )}
        </div>

        <div className={style.linkedList}>
          {selectedOwnerIds.length > 0 && (
            <>
              <h4>Selected Owners:</h4>
              <ul>
                {selectedOwnerIds.map(userId => {
                  const user = users.find(u => u.id === userId);
                  return user ? (
                    <li key={userId}>
                      <div
                        className={style.avatarSmall}
                        style={
                          user.microsoftPhotoURL
                            ? {
                                backgroundImage: `url(${user.microsoftPhotoURL})`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                                color: "transparent",
                              }
                            : {}
                        }
                      >
                        {!user.microsoftPhotoURL && user.initials}
                      </div>
                      <span>{user.displayName || user.email}</span>
                    </li>
                  ) : null;
                })}
              </ul>
            </>
          )}
        </div>

        <div className={style.actions}>
          <Button variants={["text"]} onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Owners"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default JourneyOwnersDialog;





